import { useAnalytics } from "@codat/analytics";
import {
  CheckIcon,
  InfoBanner,
  Modal,
  SelectMenu,
  SelectMenuItem,
  Typography,
  useTheme,
  useToast,
} from "@codat/orchard-ui";
import { FooterProps } from "@codat/orchard-ui/dist/es/components/Modal/components/Footer";
import { SelectMenuItemSingle } from "@codat/orchard-ui/dist/es/components/SelectMenu/model";
import { PostCompanyResponse, useAddCompanyMutation } from "api/public";
import { Product } from "api/schema/productsSchema";
import { useGetLinkURL } from "hooks/query/useGetLinkURL";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import getTryCatchErrorMessage from "utils/getTryCatchErrorMessage";
import { isValidCharacters } from "utils/regex";

import ClipboardInput from "components/ClipboardInput";
import EmailTemplateAccordion from "components/EmailTemplateAccordion";
import TextArea from "components/TextArea";
import TextInput from "components/TextInput";

import { COMPANY_DESCRIPTION_MAX_LENGTH } from "../utils/constants";
import styles from "./NewCompanyModal.module.scss";

interface NewCompanyModalProps {
  onAddCompany: (companyId: string) => void;
  onCancel: () => void;
  products: Product[];
}

export interface FormData {
  companyName: string;
  companyDescription: string;
  companyTags: string;
  assignedProducts: SelectMenuItemSingle[];
}

type ElementType<T> = T extends (infer U)[] ? U : never;
type FooterButtonProps = ElementType<FooterProps["buttons"]>;

const isSingle = (item: SelectMenuItem): item is SelectMenuItemSingle =>
  item.kind === "single";

const filterProducts = (
  products: Product[]
): {
  productsRestricted: boolean;
  filteredProducts: SelectMenuItemSingle[];
} => {
  const searchParams = new URLSearchParams(location.search);
  const newCompanyProducts = searchParams.get("newCompanyProducts");
  const assignedProducts = newCompanyProducts?.split(",");
  const productsRestricted = !!assignedProducts?.length;
  const enabledProducts = products.filter(
    (product) => product.status === "Enabled"
  );

  return {
    productsRestricted,
    filteredProducts: enabledProducts
      .filter(
        (product) =>
          !productsRestricted || assignedProducts?.includes(product.key)
      )
      .map((product) => ({
        label: product.name,
        id: product.key,
        kind: "single",
        checked:
          product.isDefault ||
          productsRestricted ||
          enabledProducts.length === 1,
      })),
  };
};

const NewCompanyModal: React.FC<NewCompanyModalProps> = ({
  onAddCompany,
  onCancel,
  products,
}) => {
  const { sendEvent } = useAnalytics();
  const { colors } = useTheme();
  const [isSubmitting, setSubmitting] = useState(false);
  const { addToast } = useToast();
  const { userState } = useUserState();
  const [newCompany, setNewCompany] = useState<PostCompanyResponse | null>(
    null
  );
  const { data: linkUrl } = useGetLinkURL({
    redirect: newCompany?.redirect,
    userId: userState?.userId,
    clientId: userState?.id,
  });
  const [{ loading }, addCompany] = useAddCompanyMutation();
  const { productsRestricted, filteredProducts } = filterProducts(products);
  const isSpendInsightsCompany = newCompany?.products.includes(
    "supplier-enablement"
  );

  const {
    handleSubmit,
    reset,
    register,
    watch,
    setValue,
    formState: { isValid, errors },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      assignedProducts: filteredProducts,
    },
  });

  const assignedProducts: SelectMenuItemSingle[] = watch("assignedProducts");

  const onAddNewCompany = ({
    companyName,
    companyDescription,
    assignedProducts,
    companyTags,
  }: FormData) => {
    setSubmitting(true);
    addCompany({
      data: {
        name: companyName,
        description: companyDescription,
        products: assignedProducts
          .filter((product) => product.checked)
          .map((product) => product.id),
        tags: companyTags ? JSON.parse(companyTags) : {},
      },
    })
      .then((company) => {
        setSubmitting(false);
        setNewCompany(company.data);
        onAddCompany?.(company.data.id);
        addToast({
          message: `Created ${company.data.name}`,
          variant: "success",
        });
      })
      .catch((e) => {
        setSubmitting(false);
        addToast({
          message: `Failed to create company: ${getTryCatchErrorMessage(e)}`,
          variant: "error",
        });
      });
  };

  const onCopy = () => {
    addToast({
      message: "Copied invite link URL to clipboard",
      variant: "success",
    });
  };

  const onClose = useCallback(() => {
    sendEvent("Close");
    onCancel();
    setNewCompany(null);
    reset();
  }, [onCancel, reset, sendEvent]);

  const name = watch("companyName");

  const buttonProps: FooterProps["buttons"] = useMemo(() => {
    const noSelectedProducts: boolean =
      assignedProducts.filter((product) => product.checked).length < 1;
    const submitButtonProps: FooterButtonProps = newCompany
      ? {
          label: "Create another company",
          onClick: () => {
            sendEvent("Create Another Button - Click");
            setNewCompany(null);
            reset();
          },
          type: "button",
          ["data-testid"]: "new-company-button-add-another",
        }
      : {
          label: "Create company",
          isLoading: loading,
          disabled: isSubmitting || !isValid || !name || noSelectedProducts,
          ["data-testid"]: "new-company-button-add",
          onClick: () => {
            sendEvent("Create Company - Submit");
          },
        };

    return [
      {
        label: newCompany ? "Close" : "Cancel",
        onClick: () => {
          sendEvent("Cancel Button - Click");
          onCancel();
        },
        variant: "secondary",
        color: "interface",
        type: "button",
        ["data-testid"]: "new-company-button-cancel",
      },
      submitButtonProps,
    ];
  }, [
    newCompany,
    loading,
    isSubmitting,
    isValid,
    name,
    onCancel,
    reset,
    sendEvent,
    assignedProducts,
  ]);

  return (
    <Modal
      onClose={onClose}
      size="small"
      data-testid="new-company-modal"
      isPinned
    >
      <>
        <Modal.Header
          title="Create a company"
          subtitle="Set up your customers in the Portal by adding them as companies."
          data-testid="new-company-modal"
          onClose={onClose}
        />
        <form onSubmit={handleSubmit(onAddNewCompany)}>
          <Modal.Body variant="light">
            <Modal.Section variant="fill">
              {!!newCompany && (
                <InfoBanner
                  color="positive"
                  className={styles.companyAddedBanner}
                  title={
                    <Typography
                      as="h3"
                      variant="heading_md"
                      className={styles.companyAddedHeader}
                    >
                      <CheckIcon fillColor={colors["color-ui-positive"]} />{" "}
                      Created: {newCompany.name}
                    </Typography>
                  }
                  subtitle={
                    <>
                      <Typography variant="body_md" as="p">
                        Send this URL to {newCompany.name} to connect their
                        platforms and grant access to their financial data
                      </Typography>
                      <ClipboardInput
                        label="Company link URL"
                        text={linkUrl}
                        onCopy={onCopy}
                        isSkeleton={loading}
                        data-testid="new-company-link-url-input"
                        inputName="Company Link URL"
                        eventProperties={{ companyId: newCompany.id }}
                      />
                      {isSpendInsightsCompany && userState && (
                        <>
                          <Typography
                            variant="body_md"
                            as="p"
                            className={styles.emailTemplateText}
                          >
                            Copy this template and paste it in an email to{" "}
                            {newCompany.name} to invite them to share their
                            financial information.
                          </Typography>
                          <EmailTemplateAccordion
                            linkUrl={linkUrl}
                            companyName={newCompany.name}
                            clientName={userState.clientName}
                          />
                        </>
                      )}
                    </>
                  }
                />
              )}
              <TextInput
                label="Company name"
                placeholder="Enter the company's name"
                disabled={!!newCompany}
                {...register("companyName", {
                  validate: (val) => {
                    if ((!!val && !val?.trim()) || !isValidCharacters(val)) {
                      return "Name contains invalid characters";
                    }
                  },
                })}
                data-testid="new-company-name-input"
                data-autofocus
                inputName="Company Name"
                error={!!errors?.companyName?.message}
                errorMessage={errors?.companyName?.message}
                required
              />
              <TextArea
                inputName="Company Description"
                label="Company description"
                showCharacterCount
                placeholder="Add a description to this company"
                maxLength={COMPANY_DESCRIPTION_MAX_LENGTH}
                className={styles.description}
                containerClassName={styles.optional}
                {...register("companyDescription", {
                  validate: (val) => {
                    if ((!!val && !val?.trim()) || !isValidCharacters(val)) {
                      return "Description contains invalid characters";
                    }
                  },
                })}
                data-testid="new-company-description-input"
                disabled={!!newCompany}
                error={!!errors?.companyDescription?.message}
                errorMessage={errors?.companyDescription?.message}
              />
              <TextArea
                inputName="Company Tags"
                label="Company tags"
                placeholder={`Add tags to this company in JSON format e.g.\n{ "region": "us", "label": "testing" }`}
                className={styles.description}
                containerClassName={styles.optional}
                {...register("companyTags", {
                  validate: (val) => {
                    if (val) {
                      try {
                        JSON.parse(val);
                      } catch (e) {
                        return "Must be valid JSON";
                      }
                    }
                  },
                })}
                data-testid="new-company-tags-input"
                disabled={!!newCompany}
                error={!!errors?.companyTags?.message}
                errorMessage={errors?.companyTags?.message}
              />
              {!newCompany &&
              !productsRestricted &&
              assignedProducts.length > 1 ? (
                <SelectMenu
                  {...register("assignedProducts")}
                  label="Add products"
                  id="Product-Menu"
                  className={styles.selectMenu}
                  items={assignedProducts}
                  onUpdate={(items) => {
                    const selectMenuItemSingles: SelectMenuItemSingle[] =
                      items.reduce<SelectMenuItemSingle[]>((acc, item) => {
                        if (isSingle(item)) {
                          acc.push(item);
                        }
                        return acc;
                      }, []);
                    setValue("assignedProducts", selectMenuItemSingles);
                  }}
                />
              ) : (
                <Typography
                  as="div"
                  variant="body_md"
                  className={styles.selectedProducts}
                >{`Added products: ${assignedProducts
                  .filter((product) => product.checked)
                  .map((product) => product.label)
                  .join(", ")}`}</Typography>
              )}
            </Modal.Section>
          </Modal.Body>
          <Modal.Footer variant="dark" buttons={buttonProps} />
        </form>
      </>
    </Modal>
  );
};

export default NewCompanyModal;
